import { useState, useEffect, useCallback } from 'react';
import { useTokenLocks, TokenLockData } from './useTokenLocks';
import { useNFTPositionsTVL } from './useNFTPositionsTVL';
import { useVestedTokens, VestedTokenData } from './useVestedTokens';

// Cache keys
const CACHE_KEY_TVL = 'mavrk_tvl_cache';
const CACHE_KEY_LOCKS = 'mavrk_locks_cache';
const CACHE_KEY_VESTED = 'mavrk_vested_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache validity

interface CachedTVLData {
  totalEcosystemTVL: number;
  tokenLocksTVL: number;
  nftPositionsTVL: number;
  vestedTVL: number;
  tokenCount: number;
  timestamp: number;
}

interface CachedLocksData {
  locks: TokenLockData[];
  timestamp: number;
}

interface CachedVestedData {
  tokens: VestedTokenData[];
  timestamp: number;
}

// Helper to safely parse JSON from localStorage
const safeJsonParse = <T>(key: string): T | null => {
  try {
    const data = localStorage.getItem(key);
    if (!data) return null;
    return JSON.parse(data) as T;
  } catch {
    return null;
  }
};

// Helper to safely store JSON in localStorage
const safeJsonStore = (key: string, data: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    console.warn(`[CachedTVL] Failed to store ${key}:`, err);
  }
};

// Check if cache is still valid
const isCacheValid = (timestamp: number): boolean => {
  return Date.now() - timestamp < CACHE_DURATION;
};

/**
 * Hook that provides cached TVL data for instant page loads
 * Shows cached data immediately while fresh data loads in background
 */
export function useCachedTVL() {
  // Get cached data for instant display
  const [cachedTVL, setCachedTVL] = useState<CachedTVLData | null>(() => {
    const cached = safeJsonParse<CachedTVLData>(CACHE_KEY_TVL);
    return cached;
  });
  
  const [cachedLocks, setCachedLocks] = useState<TokenLockData[]>(() => {
    const cached = safeJsonParse<CachedLocksData>(CACHE_KEY_LOCKS);
    return cached?.locks || [];
  });
  
  const [cachedVested, setCachedVested] = useState<VestedTokenData[]>(() => {
    const cached = safeJsonParse<CachedVestedData>(CACHE_KEY_VESTED);
    return cached?.tokens || [];
  });

  // Fetch fresh data from individual hooks
  const { locks, totalTVL: tokenLocksTVL, isLoading: isLoadingLocks, error: locksError } = useTokenLocks();
  const { totalNFTTVL, tokenCount, isLoading: isLoadingNFT, error: nftError } = useNFTPositionsTVL();
  const { tokens: vestedTokens, totalTVL: vestedTVL, isLoading: isLoadingVested, error: vestedError } = useVestedTokens();

  // Update cache when fresh data arrives
  useEffect(() => {
    if (!isLoadingLocks && locks.length > 0) {
      const cacheData: CachedLocksData = { locks, timestamp: Date.now() };
      safeJsonStore(CACHE_KEY_LOCKS, cacheData);
      setCachedLocks(locks);
    }
  }, [locks, isLoadingLocks]);

  useEffect(() => {
    if (!isLoadingVested && vestedTokens.length > 0) {
      const cacheData: CachedVestedData = { tokens: vestedTokens, timestamp: Date.now() };
      safeJsonStore(CACHE_KEY_VESTED, cacheData);
      setCachedVested(vestedTokens);
    }
  }, [vestedTokens, isLoadingVested]);

  // Update TVL cache when all data is loaded
  useEffect(() => {
    if (!isLoadingLocks && !isLoadingNFT && !isLoadingVested) {
      const totalEcosystemTVL = tokenLocksTVL + totalNFTTVL + vestedTVL;
      
      const cacheData: CachedTVLData = {
        totalEcosystemTVL,
        tokenLocksTVL,
        nftPositionsTVL: totalNFTTVL,
        vestedTVL,
        tokenCount,
        timestamp: Date.now()
      };
      
      safeJsonStore(CACHE_KEY_TVL, cacheData);
      setCachedTVL(cacheData);
      
      console.log('[CachedTVL] Updated cache with fresh data:', {
        totalEcosystemTVL: totalEcosystemTVL.toFixed(2),
        tokenLocksTVL: tokenLocksTVL.toFixed(2),
        nftPositionsTVL: totalNFTTVL.toFixed(2),
        vestedTVL: vestedTVL.toFixed(2)
      });
    }
  }, [isLoadingLocks, isLoadingNFT, isLoadingVested, tokenLocksTVL, totalNFTTVL, vestedTVL, tokenCount]);

  // Determine if we have any data to show (cached or fresh)
  const hasCachedData = cachedTVL !== null;
  const hasInitialCachedLocks = cachedLocks.length > 0;
  const hasInitialCachedVested = cachedVested.length > 0;
  const isCacheStillValid = cachedTVL ? isCacheValid(cachedTVL.timestamp) : false;

  // Return combined state
  return {
    // TVL values - use fresh if available, otherwise cached
    totalEcosystemTVL: !isLoadingLocks && !isLoadingNFT && !isLoadingVested 
      ? tokenLocksTVL + totalNFTTVL + vestedTVL 
      : cachedTVL?.totalEcosystemTVL || 0,
    tokenLocksTVL: !isLoadingLocks ? tokenLocksTVL : cachedTVL?.tokenLocksTVL || 0,
    nftPositionsTVL: !isLoadingNFT ? totalNFTTVL : cachedTVL?.nftPositionsTVL || 0,
    vestedTVL: !isLoadingVested ? vestedTVL : cachedTVL?.vestedTVL || 0,
    tokenCount: !isLoadingNFT ? tokenCount : cachedTVL?.tokenCount || 0,
    
    // Token lists - use fresh if available, otherwise cached
    locks: locks.length > 0 ? locks : cachedLocks,
    vestedTokens: vestedTokens.length > 0 ? vestedTokens : cachedVested,
    
    // Loading states - show loading only if no cached data
    isLoadingLocks: isLoadingLocks && !hasInitialCachedLocks,
    isLoadingNFT: isLoadingNFT && !hasCachedData,
    isLoadingVested: isLoadingVested && !hasInitialCachedVested,
    isRefreshing: isLoadingLocks || isLoadingNFT || isLoadingVested,
    
    // Status
    hasCachedData,
    isCacheStillValid,
    
    // Errors
    error: locksError || nftError || vestedError,
  };
}

/**
 * Clear all TVL cache (useful for manual refresh)
 */
export function clearTVLCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY_TVL);
    localStorage.removeItem(CACHE_KEY_LOCKS);
    localStorage.removeItem(CACHE_KEY_VESTED);
    console.log('[CachedTVL] Cache cleared');
  } catch (err) {
    console.warn('[CachedTVL] Failed to clear cache:', err);
  }
}

